/**
 * BUBBLER FOUNTAIN (2026-10-19, Matt: "Now a round limestone fountain" — from a landscape
 * detail: a 42" diameter, 2'-0" tall Made Form Studio concrete bubbler water feature with a
 * limestone finish, its internal bubbler sitting 2-3" above the waterline).
 *
 * A hand-formed round drum: its side and its rim wander a little (the cast piece is never a
 * perfect cylinder — the detail draws it rough), a narrow rim round a shallow water dish, the
 * water just under the rim, and the bubbler's dome of water 2½" above it at the centre. The
 * body is one material (the picked one, else limestone); the still water rides the proto's
 * translucent glass channel, the bubbler's white dome is on the solid so elevations draw it.
 *
 * Local frame (the object convention): x across centred on 0, y from the anchor (y = 0) to
 * the far side, z up from the ground — the fountain is centred at (0, d/2). The diameter is
 * min(width, depth); the height is the top of the rim.
 */
import { MeshBuilder } from '../../extrude'
import { featureEdges } from '../../edges'
import { tintMaterialId, type Pt2 } from '@aj/bim-kernel'
import type { SolidGroup } from '../opening/protos'

const LIMESTONE = '#d8cfbd'
const BUBBLE = '#eef5f6'
const SEG = 64
/** the rim's width and the dish's depth below it (feet) */
const RIM_FT = 2.5 / 12
const DISH_FT = 2 / 12
/** the water sits this far under the rim; the bubbler's dome rises this far above it */
const WATER_BELOW_RIM_FT = 0.5 / 12
export const BUBBLER_ABOVE_WATER_FT = 2.5 / 12
const BUBBLE_R_FT = 2.5 / 12

/** the hand-formed wander of the side, as a fraction of the radius, round the drum */
const wobble = (a: number) => 1 + 0.014 * Math.sin(3 * a + 1.1) + 0.008 * Math.sin(5 * a + 2.3)
/** the rim's rise and fall (feet) */
const rimWobble = (a: number) => 0.035 * Math.sin(2 * a + 0.4) + 0.02 * Math.sin(5 * a + 1.7)

export interface FountainLayout {
  cx: number
  cy: number
  /** outer radius and rim height at each of SEG angles */
  outer: Pt2[]
  inner: Pt2[]
  rimZ: number[]
  dishZ: number
  waterZ: number
  bubble: { r: number; top: number }
}

export function fountainLayout(w: number, d: number, h: number): FountainLayout {
  const R = Math.min(w, d) / 2
  const cx = 0, cy = d / 2
  const rim = Math.min(RIM_FT, R * 0.3)
  const outer: Pt2[] = [], inner: Pt2[] = [], rimZ: number[] = []
  for (let i = 0; i < SEG; i++) {
    const a = (Math.PI * 2 * i) / SEG
    const r = R * wobble(a)
    outer.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r])
    inner.push([cx + Math.cos(a) * (r - rim), cy + Math.sin(a) * (r - rim)])
    rimZ.push(h + rimWobble(a))
  }
  // the water levels at the rim's LOWEST point (where it would spill), a touch under it
  const waterZ = Math.min(...rimZ) - WATER_BELOW_RIM_FT
  return {
    cx, cy, outer, inner, rimZ,
    dishZ: Math.max(0.1, h - DISH_FT),
    waterZ,
    bubble: { r: Math.min(BUBBLE_R_FT, R * 0.3), top: waterZ + BUBBLER_ABOVE_WATER_FT },
  }
}

/** build the fountain into `b` (its body) and `glass` (the water) */
export function buildFountain(
  w: number, d: number, h: number, b: MeshBuilder, glass: MeshBuilder,
  opts: { material: string | null },
): { groups: SolidGroup[]; edges: Float32Array } {
  const L = fountainLayout(w, d, h)
  const n = SEG
  const P = (q: Pt2, z: number) => [q[0], q[1], z]
  const radial = (i: number, sign = 1) => {
    const a = (Math.PI * 2 * i) / n
    return [Math.cos(a) * sign, Math.sin(a) * sign, 0]
  }
  const body = new MeshBuilder()
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    // the side, smooth all round
    body.addTriN(P(L.outer[i], 0), P(L.outer[j], 0), P(L.outer[j], L.rimZ[j]), radial(i), radial(j), radial(j))
    body.addTriN(P(L.outer[i], 0), P(L.outer[j], L.rimZ[j]), P(L.outer[i], L.rimZ[i]), radial(i), radial(j), radial(i))
    // the rim
    body.addTri(P(L.outer[i], L.rimZ[i]), P(L.outer[j], L.rimZ[j]), P(L.inner[j], L.rimZ[j]), [0, 0, 1])
    body.addTri(P(L.outer[i], L.rimZ[i]), P(L.inner[j], L.rimZ[j]), P(L.inner[i], L.rimZ[i]), [0, 0, 1])
    // the dish's wall (facing in)
    body.addTriN(P(L.inner[i], L.dishZ), P(L.inner[i], L.rimZ[i]), P(L.inner[j], L.rimZ[j]), radial(i, -1), radial(i, -1), radial(j, -1))
    body.addTriN(P(L.inner[i], L.dishZ), P(L.inner[j], L.rimZ[j]), P(L.inner[j], L.dishZ), radial(i, -1), radial(j, -1), radial(j, -1))
    // the dish floor and the underside
    const c: Pt2 = [L.cx, L.cy]
    body.addTri(P(c, L.dishZ), P(L.inner[i], L.dishZ), P(L.inner[j], L.dishZ), [0, 0, 1])
    body.addTri(P(c, 0), P(L.outer[j], 0), P(L.outer[i], 0), [0, 0, -1])
  }

  // the water: a disc just inside the dish wall (glass channel)
  const shrink = 0.004
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    const q = (k: number): Pt2 => {
      const p = L.inner[k], dx = p[0] - L.cx, dy = p[1] - L.cy, l = Math.hypot(dx, dy) || 1
      return [p[0] - (dx / l) * shrink, p[1] - (dy / l) * shrink]
    }
    glass.addTri(P([L.cx, L.cy], L.waterZ), P(q(i), L.waterZ), P(q(j), L.waterZ), [0, 0, 1])
  }
  // the bubbler's dome of aerated (white) water — on the SOLID, in its own colour, so an
  // elevation traces its outline like the detail draws it (elevations skip the glass channel)
  const bubble = new MeshBuilder()
  const br = L.bubble.r, bh = L.bubble.top - L.waterZ, rings = 6, seg = 24
  for (let k = 0; k < rings; k++) {
    for (let i = 0; i < seg; i++) {
      const pt = (ii: number, kk: number): [number[], number[]] => {
        const th = (Math.PI / 2) * (kk / rings), ph = (Math.PI * 2 * ii) / seg
        const ux = Math.cos(th) * Math.cos(ph), uy = Math.cos(th) * Math.sin(ph), uz = Math.sin(th)
        const nn = [ux / br, uy / br, uz / bh], l = Math.hypot(nn[0], nn[1], nn[2]) || 1
        return [[L.cx + ux * br, L.cy + uy * br, L.waterZ + uz * bh], [nn[0] / l, nn[1] / l, nn[2] / l]]
      }
      const [a, na] = pt(i, k), [bb, nb] = pt(i + 1, k), [cc, nc] = pt(i + 1, k + 1), [e, ne] = pt(i, k + 1)
      bubble.addTriN(a, bb, cc, na, nb, nc)
      if (k < rings - 1) bubble.addTriN(a, cc, e, na, nc, ne)
    }
  }

  const groups: SolidGroup[] = []
  const append = (m: MeshBuilder, materialId: string) => {
    const start = b.idx.length, base = b.vertCount
    for (let i = 0; i < m.pos.length; i++) b.pos.push(m.pos[i])
    for (let i = 0; i < m.nrm.length; i++) b.nrm.push(m.nrm[i])
    for (const i of m.idx) b.idx.push(i + base)
    b.vertCount += m.vertCount
    groups.push({ start, count: m.idx.length, materialId })
  }
  append(body, opts.material || tintMaterialId(LIMESTONE, 'default'))
  append(bubble, tintMaterialId(BUBBLE, 'default'))
  // feature edges of the drum only — the dome is smooth; its outline comes from the view
  return { groups, edges: featureEdges(body.build()) }
}

/** the fountain in plan: the drum, the rim's inside edge, and the bubbler */
export function fountainPlanSymbol(w: number, d: number, h: number): { pts: Pt2[]; closed?: boolean }[] {
  const L = fountainLayout(w, d, h)
  const dot: Pt2[] = []
  for (let i = 0; i < 16; i++) {
    const a = (Math.PI * 2 * i) / 16
    dot.push([L.cx + Math.cos(a) * L.bubble.r, L.cy + Math.sin(a) * L.bubble.r])
  }
  return [{ pts: L.outer, closed: true }, { pts: L.inner, closed: true }, { pts: dot, closed: true }]
}
